import { useId, useState } from 'react';

import { isLiabilityAccountType, type TransferPurpose } from '@pfo/domain';

import { instantForTransactionDate, todayInputValue } from '../../../shared/lib/dates.js';
import { toAppError } from '../../../shared/lib/errors.js';
import forms from '../../../shared/styles/forms.module.css';
import {
  buildDebtPaymentPayload,
  buildTransferPayload,
  postDebtPayment,
  postTransfer,
  updateEntry,
  type QuickAddOptions,
  type SubjectContext,
  type TransferDraft,
} from '../data/quickAddRepository.js';
import type { ReviewSubmit } from '../model/reviewSubmit.js';
import { TRANSFER_PURPOSE_LABELS } from '../model/transactionLabels.js';
import { AmountCard } from './AmountCard.js';
import { ChipRow } from './ChipRow.js';
import { DatePicker } from './DatePicker.js';
import { missingFieldsMessage } from '../model/missingFields.js';
import { MissingHint } from './MissingHint.js';
import styles from './QuickAddControls.module.css';

export type TransferFormInitial = Readonly<{
  amount: number;
  fromAccountId: string;
  toAccountId: string;
  date: string;
  note: string;
  purpose?: string;
}>;

/**
 * "Chuyển tiền" — money moving between the household's own accounts (spec v2 22.1, shape 3),
 * and paying down what it owes (shape 5).
 *
 * Both live on one tab because they are the same gesture to the person doing it: money leaves
 * an account and lands somewhere. Choosing a card or a loan as the destination is what tells
 * the app it is a `debt_payment` — the user is never asked to know the difference (PO
 * decision, plan step 1.3).
 *
 * Net worth does not change either way, which is why neither is recorded as income on one
 * side and an expense on the other — that double count is what the single balanced entry
 * prevents (spec 11).
 *
 * With `review` it is the "Sửa" form of a bank notice waiting for approval (AL1): the draft
 * goes back to the caller. Debt payments are not offered there — a pending notice is only
 * ever an expense or a transfer (AL1 Q3), so the card/loan row would lead to a refusal.
 */
export function TransferForm({
  options,
  subjects,
  timeZone,
  entryId,
  initial,
  review,
  onSaved,
  onCancel,
}: {
  options: QuickAddOptions;
  subjects: SubjectContext;
  timeZone: string;
  entryId?: string;
  initial?: TransferFormInitial;
  review?: ReviewSubmit<TransferDraft>;
  onSaved: (message: string) => void;
  onCancel?: () => void;
}) {
  const noteId = useId();
  const purposeId = useId();

  const assetAccounts = options.accounts.filter(
    (account) => !isLiabilityAccountType(account.accountType),
  );
  const liabilityAccounts =
    review === undefined
      ? options.accounts.filter((account) => isLiabilityAccountType(account.accountType))
      : [];

  const [amount, setAmount] = useState<number | null>(initial?.amount ?? null);
  const [from, setFrom] = useState(initial?.fromAccountId ?? options.defaultAccountId ?? '');
  const [to, setTo] = useState(initial?.toAccountId ?? '');
  const [date, setDate] = useState(initial?.date ?? (() => todayInputValue(timeZone)));
  const [note, setNote] = useState(initial?.note ?? '');
  const [purpose, setPurpose] = useState<TransferPurpose | ''>(
    () => purposeOf(initial?.purpose) ?? '',
  );

  const [message, setMessage] = useState<string | null>(null);
  const [isSaving, setSaving] = useState(false);

  // The destination decides the shape: money into an account we own is a transfer, money into
  // something we owe is a payment against that debt.
  const isDebtPayment = liabilityAccounts.some((account) => account.id === to);
  const sameAccount = from !== '' && from === to;
  const canSave = amount !== null && amount > 0 && from !== '' && to !== '' && !sameAccount;

  async function handleSubmit(action: 'primary' | 'secondary' = 'primary') {
    if (!canSave || amount === null) return;

    setSaving(true);
    setMessage(null);
    try {
      const occurredAt = instantForTransactionDate(date, timeZone);
      const trimmedNote = note.trim();
      const common = {
        amount,
        fromAccountId: from,
        occurredAt,
        ...(trimmedNote === '' ? {} : { note: trimmedNote }),
      };

      if (isDebtPayment) {
        const draft = { ...common, liabilityAccountId: to };
        if (entryId === undefined) {
          await postDebtPayment(options, subjects, draft);
          onSaved('Đã lưu khoản trả nợ.');
        } else {
          await updateEntry(entryId, buildDebtPaymentPayload(options, subjects, draft));
          onSaved('Đã cập nhật khoản trả nợ.');
        }
        return;
      }

      const draft: TransferDraft = {
        ...common,
        toAccountId: to,
        ...(purpose === '' ? {} : { purpose }),
      };
      if (review !== undefined) {
        onSaved(await review.onSubmit(draft, { date, action }));
      } else if (entryId === undefined) {
        await postTransfer(options, subjects, draft);
        onSaved('Đã lưu giao dịch chuyển tiền.');
      } else {
        await updateEntry(entryId, buildTransferPayload(options, subjects, draft));
        onSaved('Đã cập nhật giao dịch chuyển tiền.');
      }
    } catch (error) {
      setMessage(toAppError(error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <form
      className={styles.form}
      onSubmit={(event) => {
        event.preventDefault();
        void handleSubmit();
      }}
      noValidate
    >
      <AmountCard
        value={amount}
        onChange={setAmount}
        autoFocus={entryId === undefined && review === undefined}
      />

      <ChipRow
        label="Từ tài khoản"
        items={assetAccounts.map((account) => ({
          id: account.id,
          label: account.name,
          accountType: account.accountType,
        }))}
        selectedId={from}
        onSelect={setFrom}
        emptyMessage="Cần ít nhất hai tài khoản để chuyển tiền."
      />

      <ChipRow
        label="Đến tài khoản"
        items={assetAccounts
          .filter((account) => account.id !== from)
          .map((account) => ({
            id: account.id,
            label: account.name,
            accountType: account.accountType,
          }))}
        selectedId={to}
        onSelect={setTo}
        emptyMessage="Cần ít nhất hai tài khoản để chuyển tiền."
      />

      {liabilityAccounts.length > 0 ? (
        <ChipRow
          label="Hoặc trả nợ / trả thẻ"
          items={liabilityAccounts.map((account) => ({
            id: account.id,
            label: account.name,
            accountType: account.accountType,
          }))}
          selectedId={to}
          onSelect={setTo}
        />
      ) : null}

      {isDebtPayment ? (
        <p className={styles.hint}>
          Khoản này ghi giảm nợ, không tính là chi tiêu — tiền đã được ghi chi lúc phát sinh.
        </p>
      ) : null}

      <DatePicker value={date} onChange={setDate} timeZone={timeZone} />

      <details className={forms.advanced}>
        <summary className={forms.summary}>Chi tiết</summary>

        <div className={forms.field}>
          <label htmlFor={noteId}>Ghi chú</label>
          <input
            id={noteId}
            className={forms.input}
            type="text"
            value={note}
            autoComplete="off"
            placeholder="Ví dụ: tiền tháng 8, đã trừ phần đi siêu thị"
            onChange={(event) => setNote(event.target.value)}
          />
          {/* B9: on a transfer the note is often the whole message to the other person. */}
          <p className={forms.hint}>Ghi chú hiển thị nổi bật ở phía tài khoản nhận.</p>
        </div>

        {/* A debt payment has only one meaning, so it is not asked for one. */}
        {isDebtPayment ? null : (
          <div className={forms.field}>
            <label htmlFor={purposeId}>Mục đích</label>
            <select
              id={purposeId}
              className={forms.input}
              value={purpose}
              onChange={(event) => setPurpose(event.target.value as TransferPurpose | '')}
            >
              <option value="">Suy từ tài khoản nhận</option>
              {Object.entries(TRANSFER_PURPOSE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
            <p className={forms.hint}>
              Quyết định khoản này vào mục Tiết kiệm, Dự phòng hay Đầu tư trong báo cáo.
            </p>
          </div>
        )}
      </details>

      {message ? (
        <p className={forms.error} role="alert">
          {message}
        </p>
      ) : null}

      <div className={styles.footer}>
        {onCancel ? (
          <button type="button" className={forms.secondary} onClick={onCancel}>
            Hủy
          </button>
        ) : null}
        {review !== undefined ? (
          <button
            type="button"
            className={forms.secondary}
            disabled={!canSave || isSaving}
            onClick={() => void handleSubmit('secondary')}
          >
            {review.secondaryLabel}
          </button>
        ) : null}
        <button type="submit" className={styles.save} disabled={!canSave || isSaving}>
          {isSaving
            ? 'Đang lưu…'
            : review !== undefined
              ? review.primaryLabel
              : entryId === undefined
                ? 'Lưu giao dịch'
                : 'Cập nhật'}
        </button>
      </div>
      <MissingHint
        message={missingFieldsMessage([
          (amount === null || amount <= 0) && 'số tiền',
          from === '' && 'tài khoản chuyển',
          to === '' && 'tài khoản nhận',
          sameAccount && 'hai tài khoản khác nhau',
        ])}
      />
    </form>
  );
}

/** The stored purpose is free text in the ledger; only a known one is offered back. */
function purposeOf(value: string | null | undefined): TransferPurpose | null {
  if (value === null || value === undefined) return null;
  return value in TRANSFER_PURPOSE_LABELS ? (value as TransferPurpose) : null;
}
